import React, { useState } from 'react';
import useWindowWidth from '../../hooks/useWindowWidth/useWindowWidth.js';
import { PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';

import Ticket from '../elements/Ticket/Ticket';
import LoadingOverlay from '../elements/LoadingOverlay/LoadingOverlay';
import { useParams, NavLink, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, withExtras, withFormulas, withParents } from '../../Utils/Functions/Dexie-Setup';
import { processAndSortItems } from '../../Utils/Functions/helpers';
import { submitDfrLocally } from '../../Utils/Functions/submission_helpers';

const ClientTicketPage = () => {
  const { id } = useParams();
  const decodedId = atob(id);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent double-clicks

    const numericId = Number(decodedId);
    if (isNaN(numericId)) return;

    setIsSubmitting(true);
    try {
      const success = await submitDfrLocally(numericId);
      if (success) {
        navigate("/"); // Redirect on success
      } else {
        alert("There was an error submitting the ticket. Please try again.");
      }
    } finally {
      setIsSubmitting(false); // Re-enable button
    }
  };

  const ticketData = useLiveQuery(async () => {
    const numericId = Number(decodedId);
    if (isNaN(numericId)) return undefined;

    const report = await db.dailyFieldReports.get(numericId)
      .then(record => record ? withExtras("dailyFieldReports", [withParents, withFormulas])([record]) : undefined)
      .then(res => res ? res[0] : undefined);

    if (!report) return undefined;

    const [equipmentLineItems, laborLineItems] = await Promise.all([
      db.equipmentLineItems.where('relatedDailyFieldReport').equals(numericId).toArray(),
      db.laborLineItems.where('relatedDailyFieldReport').equals(numericId).toArray(),
    ]);

    const formattedLabor = laborLineItems.map(item => ({
      ...item,
      type: "Labor",
      name: [item.position, item.earnCode, `${item.shift} Shift`, item.driver ? "Service Vehicle Used" : null].filter(Boolean).join(' - '),
      qty: item.hours,
      unit: "hrs",
    }));


    const combinedItems = [...formattedLabor, ...equipmentLineItems];
    const processedItems = processAndSortItems(combinedItems);

    return {
      ...report,
      items: processedItems,
      job: report.jobNumber_record || {},
    };
  }, [decodedId]);

  if (!ticketData) {
    return <LoadingOverlay />;
  }

  if (isMobile) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh', backgroundColor: '#f3f4f6', gap: '10px' }}>
        <PDFDownloadLink document={<Ticket data={ticketData} />} fileName={`${ticketData.invoiceNumber}.pdf`} style={{ textDecoration: 'none' }}>
          {({ loading }) => (
            <button style={{ backgroundColor: loading ? '#9ca3af' : '#2563eb', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s' }} disabled={loading}>
              {loading ? 'Generating PDF...' : 'Download Ticket'}
            </button>
          )}
        </PDFDownloadLink>
        <NavLink to={`/form/${id}`} style={{ backgroundColor: '#2563eb', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s', textDecoration: 'none' }}>
          Edit Ticket
        </NavLink>
        <button onClick={handleSubmit} disabled={isSubmitting} style={{ backgroundColor: isSubmitting ? '#9ca3af' : '#16a34a', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s' }}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: '10px' }}>
        <PDFDownloadLink document={<Ticket data={ticketData} />} fileName={`${ticketData.invoiceNumber}.pdf`} style={{ textDecoration: 'none' }}>
          {({ loading }) => (
            <button style={{ backgroundColor: loading ? '#9ca3af' : '#2563eb', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s' }} disabled={loading}>
              {loading ? 'Generating PDF...' : 'Download Ticket'}
            </button>
          )}
        </PDFDownloadLink>
        <NavLink to={`/form/${id}`} style={{ backgroundColor: '#2563eb', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s', marginLeft: '10px', textDecoration: 'none' }}>
          Edit Ticket
        </NavLink>
        <button onClick={handleSubmit} disabled={isSubmitting} style={{ backgroundColor: isSubmitting ? '#9ca3af' : '#16a34a', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s', marginLeft: '10px' }}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
      <PDFViewer style={{ width: '100%', height: 'calc(100vh - 70px)', border: 'none' }}>
        <Ticket data={ticketData} />
      </PDFViewer>
    </>
  );
};

export default ClientTicketPage;
